'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  CalendarBlank,
  CaretDown,
  Check,
  Lightning,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { isRestaurantOpen, DELIVERY_SLOTS, OPEN_HOUR } from '@/lib/business-hours';
import {
  useCheckoutStore,
  selectDeliveryTime,
  type DeliveryTimeType,
} from '@/stores/checkout-store';
import { useAddressStore, selectSelectedAddress } from '@/stores/address-store';

// ========== TYPES ==========

interface DeliveryTimeStepProps {
  onNext?: () => void;
  onBack?: () => void;
}

// ========== HELPER: today as YYYY-MM-DD ==========

// Локальная дата, а НЕ toISOString(): тот отдаёт UTC, и после 21:00 МСК
// «сегодня» уезжало на день вперёд — фильтр прошедших слотов переставал
// работать именно в самые загруженные часы.
const todayStr = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Минимальный запас для предзаказа. Слот нельзя выбрать, если до его начала
 * осталось меньше этого времени: кухне и курьеру нужен реальный горизонт,
 * иначе в 12:04 прилетает заказ на 12:30 и его физически не собрать.
 */
export const PREORDER_LEAD_MINUTES = 180;

// ========== HELPER: generate time slots filtered by current time ==========

/**
 * Генерирует слоты доставки: первое окно начинается в 12:30, последнее
 * заканчивается в 22:15 (то есть 21:45–22:15). Шаг и длина окна — из
 * lib/business-hours.ts, там же часы приёма заказов 11:00–22:00.
 *
 * Это ДВА разных значения, и оба верные (решение владельца по вопросу 46):
 * заказ принимается с 11:00, а первый слот доставки — 12:30. Раньше границы
 * стояли числами прямо здесь, а комментарий обещал «12:30–22:00», хотя код
 * считал до 22:15 — расхождение внутри одного файла.
 *
 * Если `filterDate` === сегодня — убирает слоты, начало которых уже прошло.
 */
const generateTimeSlots = (filterDate?: string): Record<string, string[]> => {
  const now = new Date();
  const isToday = filterDate === todayStr();
  // Самое раннее время, которое вообще можно предложить сегодня.
  const earliest = new Date(now.getTime() + PREORDER_LEAD_MINUTES * 60_000);

  // Временная блокировка слотов 12:xx с 05.03.2026 по 06.03.2026 включительно
  const isHour12Blocked =
    !!filterDate && filterDate >= '2026-03-05' && filterDate <= '2026-03-06';

  // Полная блокировка предзаказа на 08.03.2026
  const BLOCKED_DATES = ['2026-03-08'];
  if (filterDate && BLOCKED_DATES.includes(filterDate)) {
    return {};
  }

  const slots: { timeWindow: string; hour: number }[] = [];
  const cursor = new Date();
  cursor.setHours(DELIVERY_SLOTS.firstStart.hour, DELIVERY_SLOTS.firstStart.minute, 0, 0);

  const end = new Date();
  end.setHours(DELIVERY_SLOTS.lastEnd.hour, DELIVERY_SLOTS.lastEnd.minute, 0, 0);

  const fmt = (d: Date) =>
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false });

  while (cursor <= end) {
    const windowEnd = new Date(cursor);
    windowEnd.setMinutes(windowEnd.getMinutes() + DELIVERY_SLOTS.windowMinutes);

    if (windowEnd <= end) {
      // Сегодня показываем только слоты, до начала которых остался
      // минимальный запас. Раньше условие было `cursor > now`, поэтому
      // в 12:04 предлагался слот 12:30.
      if (!isToday || cursor >= earliest) {
        // Пропускаем слоты 12:xx в заблокированные даты
        if (!(isHour12Blocked && cursor.getHours() === 12)) {
          slots.push({ timeWindow: `${fmt(cursor)} - ${fmt(windowEnd)}`, hour: cursor.getHours() });
        }
      }
    }
    cursor.setMinutes(cursor.getMinutes() + DELIVERY_SLOTS.stepMinutes);
  }

  return slots.reduce((acc, s) => {
    const key = `${s.hour}:00`;
    (acc[key] ??= []).push(s.timeWindow);
    return acc;
  }, {} as Record<string, string[]>);
};

// ========== DELIVERY TYPE BUTTON ==========

function DeliveryTypeButton({
  active,
  icon: Icon,
  text,
  description,
  onClick,
  isDark,
  disabled = false,
}: {
  active: boolean;
  icon: React.ElementType;
  text: string;
  description?: string;
  onClick: () => void;
  isDark: boolean;
  disabled?: boolean;
}) {
  return (
    <motion.button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'w-full p-4 rounded-card border transition-all text-left',
        disabled
          ? 'opacity-50 cursor-not-allowed border-[var(--border-default)] bg-[var(--surface-disabled)]'
          : active
            ? 'border-[var(--primary)] bg-[var(--primary-subtle)]'
            : isDark
              ? 'border-[var(--border-default)] bg-[var(--surface-secondary)] hover:border-[var(--primary)]/50'
              : 'border-[var(--border-default)] bg-[var(--surface-primary)] hover:border-[var(--primary)]/50',
      )}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'w-10 h-10 rounded-pill flex items-center justify-center flex-shrink-0',
            active
              ? 'bg-[var(--primary)] text-[var(--text-inverse)]'
              : isDark
                ? 'bg-[var(--surface-tertiary)] text-[var(--text-secondary)]'
                : 'bg-[var(--surface-secondary)] text-[var(--text-secondary)]',
          )}
        >
          <Icon weight={active ? 'fill' : 'regular'} className="w-5 h-5" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span
              className={cn(
                'font-semibold text-sm',
                active ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
              )}
            >
              {text}
            </span>

            {active && !disabled && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-5 h-5 rounded-pill bg-[var(--primary)] flex items-center justify-center"
              >
                <Check weight="bold" className="w-3 h-3 text-[var(--text-inverse)]" />
              </motion.div>
            )}
          </div>

          {description && (
            <p className="text-xs mt-1 text-[var(--text-secondary)]">{description}</p>
          )}
        </div>
      </div>
    </motion.button>
  );
}

// ========== TIME ACCORDION ==========

function TimeAccordion({
  timeSlots,
  selectedTime,
  onSelectTime,
  isDark,
}: {
  timeSlots: Record<string, string[]>;
  selectedTime: string;
  onSelectTime: (time: string) => void;
  isDark: boolean;
}) {
  const [expandedHour, setExpandedHour] = useState<string | null>(() => {
    for (const [hour, slots] of Object.entries(timeSlots)) {
      if (slots.includes(selectedTime)) return hour;
    }
    return Object.keys(timeSlots)[0] || null;
  });

  return (
    <div className="space-y-2">
      {Object.entries(timeSlots).map(([hour, slots]) => (
        <div
          key={hour}
          className={cn(
            'rounded-card border overflow-hidden',
            isDark
              ? 'border-[var(--border-default)] bg-[var(--surface-secondary)]'
              : 'border-[var(--border-default)] bg-[var(--surface-primary)]',
          )}
        >
          <button
            onClick={() => setExpandedHour(expandedHour === hour ? null : hour)}
            className={cn(
              'w-full p-3 flex items-center justify-between hover:bg-opacity-80 transition-colors',
              expandedHour === hour && 'bg-[var(--primary-subtle)]',
            )}
          >
            <div className="flex items-center gap-2">
              <Clock
                weight={expandedHour === hour ? 'fill' : 'regular'}
                className={cn(
                  'w-4 h-4',
                  expandedHour === hour ? 'text-[var(--primary)]' : 'text-[var(--text-tertiary)]',
                )}
              />
              <span
                className={cn(
                  'font-medium text-sm',
                  expandedHour === hour ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
                )}
              >
                {hour}
              </span>
            </div>
            <motion.div animate={{ rotate: expandedHour === hour ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <CaretDown
                weight="bold"
                className={cn(
                  'w-4 h-4',
                  expandedHour === hour ? 'text-[var(--primary)]' : 'text-[var(--text-tertiary)]',
                )}
              />
            </motion.div>
          </button>

          <AnimatePresence>
            {expandedHour === hour && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-2 p-3 border-t border-[var(--border-default)]">
                  {slots.map((time) => (
                    <motion.button
                      key={time}
                      onClick={() => onSelectTime(time)}
                      className={cn(
                        'p-2.5 rounded-card text-sm font-medium transition-all',
                        selectedTime === time
                          ? 'bg-[var(--primary)] text-[var(--text-inverse)]'
                          : isDark
                            ? 'bg-[var(--surface-tertiary)] text-[var(--text-primary)] hover:bg-[var(--primary-subtle)]'
                            : 'bg-[var(--surface-secondary)] text-[var(--text-primary)] hover:bg-[var(--primary-subtle)]',
                      )}
                      whileTap={{ scale: 0.95 }}
                    >
                      {time}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function DeliveryTimeStep(_props: DeliveryTimeStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Checkout Store
  const deliveryTime = useCheckoutStore(selectDeliveryTime);
  const setDeliveryTime = useCheckoutStore((state) => state.setDeliveryTime);

  // Address Store
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const estimatedTime = selectedAddress?.estimatedTime || '40-60';

  // Preorder-only: приём заказов ещё не начался или уже закончился
  // (до 11:00 или после 22:00 — см. OPEN_HOUR/CLOSE_HOUR в business-hours.ts).
  // ВАЖНО: isRestaurantOpen() читает new Date() — на сервере и клиенте разные
  // часовые пояса (сервер UTC+3 Москва, клиенты Барнаул UTC+7). Прямой вызов
  // в render даёт React #418 hydration mismatch. Поэтому используем mount-pattern:
  // на первом рендере (SSR + начальная гидрация) — всегда false, после mount —
  // реальное значение.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const preorderOnly = mounted ? !isRestaurantOpen() : false;

  // Auto-switch to scheduled when preorder-only
  useEffect(() => {
    if (preorderOnly && deliveryTime.type === 'now') {
      setDeliveryTime({ type: 'scheduled' });
    }
  }, [preorderOnly, deliveryTime.type, setDeliveryTime]);

  // Generate time slots — filtered for today (past slots removed)
  const timeSlots = useMemo(
    () => generateTimeSlots(deliveryTime.selectedDate),
    [deliveryTime.selectedDate],
  );

  // If selected time is no longer in available slots (e.g. time passed), clear it
  useEffect(() => {
    if (!deliveryTime.selectedTime) return;
    const allSlots = Object.values(timeSlots).flat();
    if (allSlots.length > 0 && !allSlots.includes(deliveryTime.selectedTime)) {
      setDeliveryTime({ selectedTime: '' });
    }
  }, [timeSlots, deliveryTime.selectedTime, setDeliveryTime]);

  // Min/Max dates.
  // ВАЖНО: todayStr() и new Date() читают часовой пояс — на сервере и клиенте
  // могут быть разные дни (особенно у полуночных пользователей в Барнауле).
  // Используем mount-pattern чтобы избежать hydration mismatch на input[min/max].
  const minDate = useMemo(() => (mounted ? todayStr() : ''), [mounted]);
  const maxDate = useMemo(() => {
    if (!mounted) return '';
    const d = new Date();
    d.setDate(d.getDate() + 14);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, [mounted]);

  // Handlers
  const handleTypeChange = useCallback(
    (type: DeliveryTimeType) => {
      setDeliveryTime({
        type,
        ...(type === 'now' ? { selectedDate: '', selectedTime: '' } : {}),
      });
    },
    [setDeliveryTime],
  );

  const handleDateChange = useCallback(
    (date: string) => {
      // Не даём выбрать прошлую дату
      if (date < todayStr()) return;
      // Время НЕ сбрасываем: чаще всего человек меняет только день, а окно
      // хочет то же самое. Если выбранного окна на новой дате нет, его
      // снимет эффект выше — там это решается по фактическому списку слотов.
      setDeliveryTime({ selectedDate: date });
    },
    [setDeliveryTime],
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      setDeliveryTime({ selectedTime: time });
    },
    [setDeliveryTime],
  );

  const noSlotsAvailable =
    deliveryTime.type === 'scheduled' &&
    deliveryTime.selectedDate &&
    Object.values(timeSlots).flat().length === 0;

  // Проверка: дата полностью заблокирована для предзаказа
  const BLOCKED_PREORDER_DATES = ['2026-03-08'];
  const isDateBlocked =
    deliveryTime.selectedDate && BLOCKED_PREORDER_DATES.includes(deliveryTime.selectedDate);



  return (
    <div className="p-4">
      {/* Delivery Type Selection */}
      <div className="space-y-3 mb-4">
        <DeliveryTypeButton
          active={deliveryTime.type === 'now'}
          icon={Lightning}
          text="Как можно быстрее"
          description={
            preorderOnly ? `Доступно с ${OPEN_HOUR}:00` : `Доставим в течение ${estimatedTime} минут`
          }
          onClick={() => handleTypeChange('now')}
          isDark={isDark}
          disabled={preorderOnly}
        />

        <DeliveryTypeButton
          active={deliveryTime.type === 'scheduled'}
          icon={CalendarBlank}
          text="Предварительный заказ"
          description="Выберите удобную дату и время"
          onClick={() => handleTypeChange('scheduled')}
          isDark={isDark}
        />
      </div>

      {/* Date & Time Selection (for scheduled) */}
      <AnimatePresence>
        {deliveryTime.type === 'scheduled' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 overflow-hidden"
          >
            {/* Date Input */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--text-primary)]">
                Дата доставки
              </label>
              <input
                type="date"
                value={deliveryTime.selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                min={minDate}
                max={maxDate}
                className={cn(
                  'w-full p-3 rounded-card border text-[16px] font-medium',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/40 focus:border-[var(--primary)]',
                  isDark
                    ? 'bg-[var(--surface-secondary)] border-[var(--border-default)] text-[var(--text-primary)]'
                    : 'bg-[var(--surface-primary)] border-[var(--border-default)] text-[var(--text-primary)]',
                )}
              />
            </div>

            {/* Time Selection */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--text-primary)]">
                Время доставки
              </label>

              {noSlotsAvailable ? (
                <div className="p-4 rounded-card bg-[var(--warning-bg)] text-[var(--warning-text)] text-sm text-center">
                  {isDateBlocked
                    ? 'Предзаказ на эту дату недоступен. Выберите другую дату.'
                    : 'На сегодня нет доступных слотов. Выберите другую дату.'}
                </div>
              ) : (
                <TimeAccordion
                  timeSlots={timeSlots}
                  selectedTime={deliveryTime.selectedTime}
                  onSelectTime={handleTimeChange}
                  isDark={isDark}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
